import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type RefObject,
} from 'react';
import type { Camera, Celula, ColunaSugestao, PalavraColocada, Sugestao } from '@core/tipos';
import type { IndiceLexico } from '@core/lexico/indice';
import { caracteresCanonicos } from '@core/lexico/normalizacao';
import type { EstadoJogo } from '@core/motor/estados';
import { AFASTAMENTO_PREVIEW, encaixeAtivoDe } from '@core/motor/estados';
import type { Evento } from '@core/motor/eventos';
import { anunciarSugestao } from '@core/motor/mensagens';
import { multiplicadorDaAncora } from '@core/modos/trama';
import type { EstadoLetra } from '@core/termo/avaliacao';
import { marcasDaCadeia } from '@core/termo/partida';
import { NOME_ESTADO } from '@core/termo/mensagens';
import { LADO_CELULA, regiaoVisivel } from '@core/tabuleiro/camera';
import {
  celulaNoIndice,
  celulasDe,
  mesmaCelula,
  regiaoDeCelulas,
  regioesSeCruzam,
} from '@core/tabuleiro/coordenadas';
import { construirGrade, ultimaPalavra } from '@core/tabuleiro/grade';
import { ancorasElegiveis } from '@core/tabuleiro/posicionamento';
import type { UsoCamera } from '../ganchos/useCamera';

/**
 * Tabuleiro (§7.2).
 *
 * Renderiza em DOM, não em `<canvas>`: sugestões e letras da ponte precisam ser
 * focáveis por `Tab`, anunciáveis por leitor de tela e ter alvo de toque de
 * 44 px (§13, T03-06, T04-04, T04-05). Ver ADR-0002.
 *
 * A câmera é uma única transformação no elemento `.tabuleiro__mundo`, então
 * arrastar e dar zoom não tocam em nenhuma coordenada de palavra (T07-01).
 */

/** Margem de virtualização: duas viewports em cada direção (§16). */
const MARGEM_VIEWPORTS = 2;

/**
 * Tempo que as sugestões descartadas ficam em cena saindo (T04-02).
 * Teto da faixa de §15 (180–240 ms); a transição em si dura `--dur-saida`.
 */
const DURACAO_SAIDA_MS = 240;

/** Abaixo deste zoom, palavras históricas são simplificadas (T07-07). */
const ZOOM_LONGE = 0.75;

function estiloDaCelula(c: Celula): CSSProperties {
  return { left: c.coluna * LADO_CELULA, top: c.linha * LADO_CELULA };
}

/**
 * A primeira palavra da cadeia é a que o jogador escreveu do nada, sem âncora a
 * obedecer — o turno 0. Ela ganha marca própria e não desbota junto com o resto
 * do histórico: é a origem do tabuleiro (§15, T07-09).
 */
function ehOrigem(palavra: PalavraColocada): boolean {
  return palavra.turno === 0;
}

function classePalavra(palavra: PalavraColocada, ehRecente: boolean): string {
  const origem = ehOrigem(palavra) ? ' celula--origem' : '';
  if (!ehRecente) return `celula celula--historico${origem}`;
  return palavra.papel === 'ponte' ? 'celula celula--ponte' : `celula celula--base${origem}`;
}

function Palavra({
  palavra,
  ehRecente,
  marcas,
}: {
  palavra: PalavraColocada;
  ehRecente: boolean;
  /** §23: verde, amarelo e cinza da palavra do dia. Só na Trama. */
  marcas?: readonly EstadoLetra[];
}) {
  const letras = caracteresCanonicos(palavra.texto);
  return (
    <>
      {letras.map((letra, i) => {
        const marca = marcas?.[i];
        return (
          <span
            key={`${palavra.id}-${i}`}
            className={classePalavra(palavra, ehRecente)}
            data-marca={marca}
            style={estiloDaCelula(celulaNoIndice(palavra.origem, palavra.orientacao, i))}
            // A célula marcada deixa de ser decoração: ela carrega a única
            // informação do modo, e §13 proíbe que ela dependa só de cor.
            aria-hidden={marca === undefined ? 'true' : undefined}
            {...(marca === undefined
              ? {}
              : { role: 'img', 'aria-label': `${letra}, ${NOME_ESTADO[marca]}` })}
          >
            {letra}
          </span>
        );
      })}
    </>
  );
}

function Coluna({
  coluna,
  selecionavel,
  saindo,
  aoEscolher,
}: {
  coluna: ColunaSugestao;
  selecionavel: boolean;
  /** T04-02: a sugestão foi descartada e está desaparecendo. */
  saindo: boolean;
  aoEscolher: (id: string) => void;
}) {
  if (coluna.tipo === 'indisponivel') {
    // T02-01: marcador explícito de indisponibilidade, nunca ausência silenciosa.
    const rotulo =
      coluna.coluna.motivo === 'hifen'
        ? 'Hífen não recebe cruzamento'
        : coluna.coluna.motivo === 'ja-cruzada'
          ? 'Célula já atravessada pela ponte anterior'
          : 'Sem cruzamento para esta letra';
    return (
      <span
        className="indisponivel"
        style={estiloDaCelula({
          coluna: coluna.coluna.cruzamento.coluna,
          linha: coluna.coluna.cruzamento.linha + 1,
        })}
        title={rotulo}
      >
        <span className="apenas-leitor">{rotulo}</span>
        <span aria-hidden="true">·</span>
      </span>
    );
  }

  const { sugestao } = coluna;
  const letras = caracteresCanonicos(sugestao.texto);
  const letraCruzada = letras[sugestao.indiceNoTexto] ?? '';

  return (
    <div
      className="coluna"
      data-selecionavel={selecionavel && !saindo}
      data-saindo={saindo}
      data-sugestao={sugestao.texto}
      style={estiloDaCelula(sugestao.origem)}
      aria-hidden={saindo ? 'true' : undefined}
    >
      <div className="coluna__letras">
        {letras.map((letra, i) => (
          <span
            key={`${sugestao.id}-${i}`}
            className={
              i === sugestao.indiceNoTexto
                ? 'celula celula--sugestao celula--cruzamento'
                : 'celula celula--sugestao'
            }
            style={estiloDaCelula({ coluna: 0, linha: i })}
            aria-hidden="true"
          >
            {letra}
          </span>
        ))}
      </div>

      {!saindo && (
        <button
          type="button"
          className="coluna__ativar"
          style={{ height: letras.length * LADO_CELULA, width: LADO_CELULA }}
          disabled={!selecionavel}
          onClick={() => aoEscolher(sugestao.id)}
          onPointerDown={(e: PointerEvent) => e.stopPropagation()}
        >
          <span className="apenas-leitor">
            {anunciarSugestao(sugestao.texto, letraCruzada, sugestao.indiceNaBase)}
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * Sugestões descartadas ao escolher uma ponte (T04-02, §5.7).
 *
 * Quando a fase vira `escolher-ancora`, as colunas do estado somem de uma vez.
 * Aqui elas ficam em cena por `DURACAO_SAIDA_MS` com `data-saindo`, para que a
 * transição de saída de §15 aconteça — menos a sugestão escolhida, que já
 * virou ponte e não pode sumir e reaparecer.
 */
function useColunasSaindo(estado: EstadoJogo): readonly Sugestao[] {
  const [saindo, setSaindo] = useState<readonly Sugestao[]>([]);
  const anteriores = useRef<readonly ColunaSugestao[]>(estado.colunas);

  useEffect(() => {
    const antes = anteriores.current;
    anteriores.current = estado.colunas;

    if (estado.fase !== 'escolher-ancora' || antes.length === 0) return;

    const ponte = ultimaPalavra(estado.cadeia);
    const descartadas: Sugestao[] = [];
    for (const coluna of antes) {
      if (coluna.tipo !== 'sugestao') continue;
      const { sugestao } = coluna;
      const virouPonte =
        ponte !== undefined &&
        ponte.texto === sugestao.texto &&
        mesmaCelula(ponte.origem, sugestao.origem);
      if (!virouPonte) descartadas.push(sugestao);
    }

    setSaindo(descartadas);
    const id = window.setTimeout(() => setSaindo([]), DURACAO_SAIDA_MS);
    return () => window.clearTimeout(id);
  }, [estado.fase, estado.colunas, estado.cadeia]);

  return estado.fase === 'escolher-ancora' ? saindo : [];
}

export function Tabuleiro({
  estado,
  indice,
  camera,
  animando,
  arrastando,
  viewport,
  manipuladores,
  elementoRef,
  posicaoCaret,
  despachar,
}: {
  estado: EstadoJogo;
  indice: IndiceLexico;
  camera: Camera;
  animando: boolean;
  arrastando: boolean;
  viewport: { largura: number; altura: number };
  manipuladores: UsoCamera['manipuladores'];
  elementoRef: RefObject<HTMLDivElement | null>;
  posicaoCaret: number;
  despachar: (evento: Evento) => void;
}) {
  const letrasDraft = caracteresCanonicos(estado.draft);
  const selecionavel = estado.fase === 'palavra-valida';
  const colunasSaindo = useColunasSaindo(estado);

  /* Virtualização (§16): só palavras a até duas viewports de distância. */
  const palavrasVisiveis = useMemo(() => {
    const visivel = regiaoVisivel(camera, viewport.largura, viewport.altura);
    const larguraCel = Math.max(1, visivel.colunaMax - visivel.colunaMin + 1);
    const alturaCel = Math.max(1, visivel.linhaMax - visivel.linhaMin + 1);
    const area = {
      colunaMin: visivel.colunaMin - larguraCel * MARGEM_VIEWPORTS,
      colunaMax: visivel.colunaMax + larguraCel * MARGEM_VIEWPORTS,
      linhaMin: visivel.linhaMin - alturaCel * MARGEM_VIEWPORTS,
      linhaMax: visivel.linhaMax + alturaCel * MARGEM_VIEWPORTS,
    };
    return estado.cadeia.palavras.filter((p) =>
      regioesSeCruzam(regiaoDeCelulas(celulasDe(p)), area),
    );
  }, [estado.cadeia, camera, viewport.largura, viewport.altura]);

  /* Letras da ponte que podem virar âncora (T04). */
  const ancoras = useMemo(() => {
    if (estado.fase !== 'escolher-ancora') return [];
    const ponte = ultimaPalavra(estado.cadeia);
    if (ponte === undefined || ponte.papel !== 'ponte') return [];
    return ancorasElegiveis(ponte, estado.intersecaoDaPonte, indice, construirGrade(estado.cadeia));
  }, [estado.fase, estado.cadeia, estado.intersecaoDaPonte, indice]);

  /* §22.4: no Átrio, a letra da ponte mostra quanto vale apostar nela — a
     escolha da âncora é a aposta, e ela precisa ser visível antes do clique. */
  const apostas = useMemo(() => {
    if (estado.modo !== 'trama') return new Map<number, number>();
    return new Map(
      ancoras.map((a) => [a.indiceNaPonte, multiplicadorDaAncora(indice, a.caractere)]),
    );
  }, [estado.modo, ancoras, indice]);

  /* §23: as marcas do dia são derivadas, não guardadas — a cadeia e a resposta
     bastam para recalculá-las, e estado derivado é estado que pode divergir. */
  const marcas = useMemo(
    () => (estado.modo === 'termo' ? marcasDaCadeia(estado.cadeia, estado.resposta) : undefined),
    [estado.modo, estado.cadeia, estado.resposta],
  );

  /**
   * Ordem de pintura das palavras (§23).
   *
   * As células são todas `position: absolute` na mesma pilha, sem `z-index`:
   * quem vem depois no DOM cobre quem veio antes. A ponte nasce um turno
   * depois da base que ela atravessa, então ela pintava por cima justamente a
   * célula do cruzamento — e é ali que o amarelo mais aparece, porque a letra
   * que a vertical aproveitou dificilmente está na posição certa da resposta.
   *
   * A marca é a única informação do modo (§23.3), então é a última a ser
   * pintada. Fora da Trama não há marca, e a ordem da cadeia fica como
   * estava — lá o cruzamento é da ponte, e é assim que ele deve se ler.
   */
  const palavrasEmOrdem = useMemo(() => {
    if (marcas === undefined) return palavrasVisiveis;
    const marcadas = palavrasVisiveis.filter((p) => marcas.has(p.id));
    if (marcadas.length === 0) return palavrasVisiveis;
    return [...palavrasVisiveis.filter((p) => !marcas.has(p.id)), ...marcadas];
  }, [palavrasVisiveis, marcas]);

  const turnoRecente = estado.cadeia.palavras.length - 2;

  /* T06-04: ocorrência ativa da âncora e as alternativas clicáveis. */
  const encaixeAtivo = estado.draftProvisorio ? undefined : encaixeAtivoDe(estado);
  const encaixesAlternativos =
    estado.ancora !== undefined && !estado.draftProvisorio
      ? estado.encaixes.filter((e) => e.valido && e !== encaixeAtivo)
      : [];

  /* T05 / §5.11: o laço vai do centro da célula da âncora até a borda da faixa,
     seja ela à direita (padrão) ou à esquerda (quando a direita está tomada). */
  const larguraFaixa = Math.max(1, letrasDraft.length);
  const lacoParaEsquerda =
    estado.ancora !== undefined && estado.origemDraft.coluna < estado.ancora.celula.coluna;
  const celulasDeLaco =
    estado.ancora === undefined
      ? AFASTAMENTO_PREVIEW
      : lacoParaEsquerda
        ? estado.ancora.celula.coluna - (estado.origemDraft.coluna + larguraFaixa - 1)
        : estado.origemDraft.coluna - estado.ancora.celula.coluna;
  const larguraLaco = celulasDeLaco * LADO_CELULA - LADO_CELULA / 2;

  const caret = (
    <span
      className="caret"
      aria-hidden="true"
      style={{
        left: (estado.origemDraft.coluna + posicaoCaret) * LADO_CELULA - 1,
        top: estado.origemDraft.linha * LADO_CELULA + LADO_CELULA * 0.21,
      }}
    />
  );

  return (
    <div
      ref={elementoRef}
      className="tabuleiro"
      data-arrastando={arrastando}
      data-fase={estado.fase}
      {...manipuladores}
    >
      <div
        className="tabuleiro__mundo"
        data-animando={animando}
        data-longe={camera.zoom < ZOOM_LONGE}
        style={{
          transform: `translate3d(${camera.x}px, ${camera.y}px, 0) scale(${camera.zoom})`,
        }}
      >
        {palavrasEmOrdem.map((palavra) => (
          <Palavra
            key={palavra.id}
            palavra={palavra}
            ehRecente={palavra.turno >= turnoRecente}
            marcas={marcas?.get(palavra.id)}
          />
        ))}

        {/* T05-02 / §5.11: enquanto não contém a âncora, o draft flutua preso
            à célula escolhida e não ocupa células permanentes. */}
        {estado.draftProvisorio ? (
          <div
            className="draft-flutuante"
            data-vazio={letrasDraft.length === 0}
            data-lado={lacoParaEsquerda ? 'esquerda' : 'direita'}
            style={{
              ...estiloDaCelula(estado.origemDraft),
              width: larguraFaixa * LADO_CELULA,
              height: LADO_CELULA,
            }}
            aria-hidden="true"
          >
            <span className="draft-flutuante__laco" style={{ width: larguraLaco }} />
            {letrasDraft.map((letra, i) => (
              <span
                key={`draft-${i}`}
                className="celula celula--draft"
                style={estiloDaCelula({ coluna: i, linha: 0 })}
              >
                {letra}
              </span>
            ))}
          </div>
        ) : (
          /* Draft encaixado: a única cor de realce do tabuleiro (§15). */
          letrasDraft.map((letra, i) => (
            <span
              key={`draft-${i}`}
              className={
                encaixeAtivo !== undefined && i === encaixeAtivo.indiceNoDraft
                  ? 'celula celula--draft celula--encaixe'
                  : 'celula celula--draft'
              }
              style={estiloDaCelula(celulaNoIndice(estado.origemDraft, 'horizontal', i))}
              aria-hidden="true"
            >
              {letra}
            </span>
          ))
        )}

        {caret}

        {/* T06-04: clicar em outra ocorrência válida a coloca sobre a âncora. */}
        {encaixesAlternativos.map((encaixe) => (
          <button
            key={`encaixe-${encaixe.indiceNoDraft}`}
            type="button"
            className="encaixe-alvo"
            style={estiloDaCelula(
              celulaNoIndice(estado.origemDraft, 'horizontal', encaixe.indiceNoDraft),
            )}
            onClick={() =>
              despachar({ tipo: 'escolher-encaixe', indiceNoDraft: encaixe.indiceNoDraft })
            }
            onPointerDown={(e: PointerEvent) => e.stopPropagation()}
            title="Encaixar esta ocorrência sobre a âncora"
          >
            <span className="apenas-leitor">
              Encaixar a ocorrência {encaixe.indiceNoDraft + 1} da letra {estado.ancora?.caractere}{' '}
              sobre a âncora
            </span>
          </button>
        ))}

        {estado.colunas.map((coluna) => (
          <Coluna
            key={
              coluna.tipo === 'sugestao'
                ? coluna.sugestao.id
                : `x${coluna.coluna.cruzamento.coluna}:${coluna.coluna.cruzamento.linha}`
            }
            coluna={coluna}
            selecionavel={selecionavel}
            saindo={false}
            aoEscolher={(id) => despachar({ tipo: 'escolher-sugestao', id })}
          />
        ))}

        {/* T04-02: as sugestões não escolhidas desaparecem em 180–240 ms. As
            chaves são as mesmas de antes, então o DOM é reaproveitado e a
            transição de saída parte do estado visível. */}
        {colunasSaindo.map((sugestao) => (
          <Coluna
            key={sugestao.id}
            coluna={{ tipo: 'sugestao', sugestao }}
            selecionavel={false}
            saindo
            aoEscolher={() => undefined}
          />
        ))}

        {/* Interseção anterior: marcada com ×, fora da ordem de foco (T04-03). */}
        {estado.fase === 'escolher-ancora' && estado.intersecaoDaPonte !== undefined && (
          <span
            className="marca-ancora marca-ancora--usada"
            style={estiloDaCelula(estado.intersecaoDaPonte)}
          >
            <span className="apenas-leitor">Interseção já utilizada</span>
            <span aria-hidden="true">×</span>
          </span>
        )}

        {/* T05-01: a âncora escolhida fica marcada enquanto o draft a espera
            e depois de encaixar — o texto da linha de feedback é a indicação
            principal; este marcador é redundante, não informativo. */}
        {estado.ancora !== undefined && (
          <span
            className="marca-ancora marca-ancora--ativa"
            style={estiloDaCelula(estado.ancora.celula)}
            aria-hidden="true"
          >
            ●
          </span>
        )}

        {ancoras.map((ancora) => {
          const aposta = apostas.get(ancora.indiceNaPonte) ?? 1;
          return (
            <span
              key={`marca-${ancora.indiceNaPonte}`}
              className={
                aposta > 1
                  ? 'marca-ancora marca-ancora--livre marca-ancora--aposta'
                  : 'marca-ancora marca-ancora--livre'
              }
              style={estiloDaCelula(ancora.celula)}
              aria-hidden="true"
            >
              {aposta > 1 ? `×${aposta}` : '○'}
            </span>
          );
        })}

        {ancoras.map((ancora) => (
          <button
            key={`ancora-${ancora.indiceNaPonte}`}
            type="button"
            className="ancora-alvo"
            style={estiloDaCelula(ancora.celula)}
            onClick={() => despachar({ tipo: 'escolher-ancora', celula: ancora.celula })}
            onPointerDown={(e: PointerEvent) => e.stopPropagation()}
          >
            <span className="apenas-leitor">
              Continuar pela letra {ancora.caractere}, posição {ancora.indiceNaPonte + 1}
              {(apostas.get(ancora.indiceNaPonte) ?? 1) > 1 &&
                `, multiplicador ${apostas.get(ancora.indiceNaPonte)}`}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
